import { useEffect } from "react";
import { X } from "lucide-react";
import { useWorkspace } from "@/nav/WorkspaceContext";
import { WORKSPACE_ITEMS } from "@/nav/workspaceConfig";
import { NAV_ITEMS } from "@/nav/navConfig";
import { Switch } from "@/components/ui/switch";

export default function CustomizeSidebarModal() {
  const { pickerOpen, closePicker, isEnabled, toggle } = useWorkspace();

  useEffect(() => {
    if (!pickerOpen) return;
    const onKey = (e) => e.key === "Escape" && closePicker();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [pickerOpen, closePicker]);

  if (!pickerOpen) return null;

  return (
    <div
      data-testid="customize-sidebar-modal"
      onClick={closePicker}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(6, 12, 20, 0.72)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        animation: "enterUp 200ms ease-out",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 440,
          maxHeight: "88vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          background: "#162333",
          border: "1px solid #1E3048",
          borderRadius: 12,
          boxShadow: "0 24px 64px rgba(0, 0, 0, 0.55)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between px-5 pt-5 pb-4"
          style={{ borderBottom: "1px solid var(--border-divider)" }}
        >
          <div>
            <h2
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 700,
                fontSize: 16,
                letterSpacing: "-0.01em",
                color: "var(--text-primary)",
                marginBottom: 4,
              }}
            >
              Customize Sidebar
            </h2>
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 12.5,
                color: "var(--text-secondary)",
              }}
            >
              Choose which pages appear in your nav.
            </p>
          </div>
          <button
            data-testid="customize-sidebar-close"
            onClick={closePicker}
            className="flex items-center justify-center rounded-md flex-shrink-0"
            style={{
              width: 28,
              height: 28,
              background: "var(--bg-elevated)",
              border: "1px solid var(--border-divider)",
              color: "var(--text-secondary)",
              cursor: "pointer",
              marginLeft: 12,
            }}
          >
            <X size={13} />
          </button>
        </div>

        {/* Toggle list */}
        <div style={{ overflowY: "auto", padding: "8px 10px" }}>
          {WORKSPACE_ITEMS.map((item) => {
            const navItem = NAV_ITEMS.find((n) => n.path === item.path);
            const on = isEnabled(item.path);
            return (
              <div
                key={item.path}
                data-testid={`workspace-toggle-${navItem?.testId ?? item.path}`}
                className="flex items-center justify-between"
                style={{ padding: "10px 8px", borderRadius: 8 }}
              >
                <div style={{ paddingRight: 12 }}>
                  <div
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 700,
                      fontSize: 14,
                      color: "#F0F6FF",
                    }}
                  >
                    {navItem?.label ?? item.path}
                  </div>
                  <div
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: 12,
                      color: "#7A9BB5",
                      marginTop: 2,
                    }}
                  >
                    {item.description}
                  </div>
                </div>
                <Switch
                  checked={on}
                  onCheckedChange={() => toggle(item.path)}
                  data-testid={`workspace-switch-${navItem?.testId ?? item.path}`}
                />
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div
          className="px-5 py-4 flex items-center justify-end"
          style={{ borderTop: "1px solid var(--border-divider)" }}
        >
          <button
            data-testid="customize-sidebar-done"
            onClick={closePicker}
            className="inline-flex items-center rounded-lg"
            style={{
              padding: "9px 18px",
              background: "linear-gradient(135deg, #00C2A8 0%, #0EA5E9 100%)",
              color: "#0F1923",
              fontFamily: "Inter, sans-serif",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "-0.005em",
              border: "none",
              cursor: "pointer",
              boxShadow: "0 0 18px rgba(0, 194, 168, 0.30)",
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
